import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface RequirementRow {
  readonly requirementId: string;
  readonly requirementCode: string;
  readonly title: string;
  readonly requirementType: string;
  readonly currentVersionNo: number;
  readonly priority: string;
  readonly status: string;
  readonly isTraceable: boolean;
}

const TYPES = ['', 'BRD', 'FRD', 'USER_STORY', 'BUSINESS_RULE', 'NON_FUNCTIONAL'];
const STATUSES = ['', 'DRAFT', 'REVIEW', 'APPROVED', 'BASELINE', 'REVISED', 'ARCHIVED', 'RETIRED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M33 — Requirements Repository (§31 / §9 / §24). Catalogue of business
 * requirements (T435) with artefact type, current version number, priority and
 * the seven-state version-control lifecycle. Supports authoring a requirement
 * and driving it — submit, approve, baseline, revise, archive and retire — each
 * wired to the guarded /v1/pm/requirements write API. Filterable by requirement
 * type and status.
 */
export function RequirementsRepository(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<RequirementRow[]>([]);
  const [requirementType, setRequirementType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('BRD');
  const [newPriority, setNewPriority] = useState('MEDIUM');
  const [summaries, setSummaries] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (requirementType) params.set('requirementType', requirementType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: RequirementRow[] }>(`/v1/pm/requirements${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, requirementType, status, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m33.req.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/pm/requirements`, {
        method: 'POST',
        body: { title: newTitle.trim(), requirementType: newType, priority: newPriority },
        token,
      });
      setNotice(t('m33.req.created', { name: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'REQUIREMENT_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: RequirementRow, path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/requirements/${r.requirementId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m33.common.notice', { code: r.requirementCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  return (
    <main aria-label="requirements-repository">
      <h1>{t('m33.req.title')}</h1>

      <section aria-label="create-requirement">
        <h2>{t('m33.req.createHeading')}</h2>
        <form aria-label="requirement-form" onSubmit={(e) => void create(e)}>
          <label>{t('m33.common.title')}<input aria-label="field-requirement-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m33.common.type')}
            <select aria-label="field-requirement-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m33.common.priority')}
            <select aria-label="field-requirement-priority" value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-requirement" disabled={busy}>{busy ? t('m33.req.creating') : t('m33.req.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="requirement-error">{actionError}</p>}
        {notice && <p aria-label="requirement-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m33.common.type')}
          <select aria-label="filter-requirement-type" value={requirementType} onChange={(e) => setRequirementType(e.target.value)}>
            {TYPES.map((ty) => <option key={ty || 'all'} value={ty}>{ty || t('m33.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m33.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m33.common.loading')}</p> : (
        <>
          <p aria-label="requirement-count">{t('m33.req.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="requirement-table"
            rows={rows}
            rowKey={(r) => r.requirementId}
            columns={[
              { key: 'requirementCode', header: t('m33.common.code'), render: (r) => r.requirementCode },
              { key: 'title', header: t('m33.common.title'), render: (r) => r.title },
              { key: 'requirementType', header: t('m33.common.type'), render: (r) => r.requirementType },
              { key: 'currentVersionNo', header: t('m33.common.version'), render: (r) => <>v{r.currentVersionNo}</> },
              { key: 'priority', header: t('m33.common.priority'), render: (r) => r.priority },
              { key: 'isTraceable', header: t('m33.req.traceable'), render: (r) => r.isTraceable ? t('m33.common.yes') : t('m33.common.no') },
              { key: 'status', header: t('m33.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m33.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`submit-${r.requirementCode}`} onClick={() => void act(r, 'submit', t('m33.req.act.submitted'), 'REQUIREMENT_MANAGE')}>{t('m33.req.btnSubmit')}</Button>
                  )}
                  {r.status === 'REVIEW' && (
                    <Button variant="secondary" type="button" aria-label={`approve-${r.requirementCode}`} onClick={() => void act(r, 'approve', t('m33.req.act.approved'), 'REQUIREMENT_APPROVE')}>{t('m33.req.btnApprove')}</Button>
                  )}
                  {r.status === 'APPROVED' && (
                    <Button variant="secondary" type="button" aria-label={`baseline-${r.requirementCode}`} onClick={() => void act(r, 'baseline', t('m33.req.act.baselined'), 'REQUIREMENT_APPROVE')}>{t('m33.req.btnBaseline')}</Button>
                  )}
                  {(r.status === 'BASELINE' || r.status === 'REVISED' || r.status === 'APPROVED') && (
                    <>
                      <input aria-label={`change-summary-${r.requirementCode}`} value={summaries[r.requirementId] ?? ''} onChange={(e) => setSummaries((m) => ({ ...m, [r.requirementId]: e.target.value }))} placeholder={t('m33.req.changeSummaryPh')} />
                      <Button variant="secondary" type="button" aria-label={`revise-${r.requirementCode}`} onClick={() => void act(r, 'revise', t('m33.req.act.revised'), 'REQUIREMENT_MANAGE', { changeSummary: summaries[r.requirementId] ?? '' })}>{t('m33.req.btnRevise')}</Button>
                    </>
                  )}
                  {!['ARCHIVED', 'RETIRED'].includes(r.status) && (
                    <>
                      <Button variant="danger" type="button" aria-label={`archive-${r.requirementCode}`} onClick={() => void act(r, 'archive', t('m33.req.act.archived'), 'REQUIREMENT_MANAGE')}>{t('m33.req.btnArchive')}</Button>
                      <Button variant="danger" type="button" aria-label={`retire-${r.requirementCode}`} onClick={() => void act(r, 'retire', t('m33.req.act.retired'), 'REQUIREMENT_MANAGE')}>{t('m33.req.btnRetire')}</Button>
                    </>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
